import { motion } from "framer-motion";
import { ArrowRight, Bed, ChefHat, Bath, Wifi, Flame, Trees, Waves, MapPin } from "lucide-react";
import sonsGeronimoImage from "@/assets/sons-geronimo-waterfront.jpg";
import sonsRioCiboloImage from "@/assets/sons-rio-cibolo.jpg";

const geronimoFeatures = [
  { icon: Bed, label: "2 queen beds + 2 day beds" },
  { icon: ChefHat, label: "Full kitchen" },
  { icon: Bath, label: "Private bath, hot shower" },
  { icon: Wifi, label: "A/C + Wi-Fi" },
  { icon: Flame, label: "BBQ pit, fire pit, picnic table" },
  { icon: Trees, label: "Large porch on the creek" },
];

const cibooFeatures = [
  { icon: Bed, label: "Glamping cabins, bell tents & family cabins" },
  { icon: Waves, label: "2 miles of spring-fed Cibolo Creek" },
  { icon: Trees, label: "100+ acres of Hill Country" },
  { icon: Flame, label: "Fire pits & BBQ pits at every site" },
  { icon: Wifi, label: "Kayaks & paddleboards on-site" },
  { icon: MapPin, label: "20 min from San Antonio" },
];

const OvernightSisterProperties = () => {
  return (
    <section className="py-20 lg:py-28 bg-secondary/40">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 max-w-3xl mx-auto"
        >
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Want to Make It a Weekend? Stay Overnight Nearby
          </h2>
          <p className="text-lg text-muted-foreground">
            Son's Island is day-use only this season — but our two nearby sister properties on
            spring-fed creeks have you covered for the night.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Son's Geronimo */}
          <motion.a
            href="https://sonsgeronimo.com"
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="group block bg-background rounded-2xl overflow-hidden border border-border/50 shadow-soft hover:shadow-medium transition-all hover:-translate-y-1"
          >
            <img
              src={sonsGeronimoImage}
              alt="Son's Geronimo waterfront cabin on spring-fed Geronimo Creek"
              loading="lazy"
              decoding="async"
              className="w-full h-56 object-cover"
            />
            <div className="p-6 md:p-8">
              <h3 className="font-display text-2xl font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
                Son's Geronimo
              </h3>
              <p className="text-base text-muted-foreground mb-5">
                21 identical creekside cabins on spring-fed Geronimo Creek.
              </p>
              <ul className="space-y-2 mb-6">
                {geronimoFeatures.map(({ icon: Icon, label }) => (
                  <li key={label} className="flex items-start gap-2 text-sm text-foreground">
                    <Icon className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                    <span>{label}</span>
                  </li>
                ))}
              </ul>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-accent group-hover:gap-2 transition-all">
                View Cabins at sonsgeronimo.com
                <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </motion.a>

          {/* Son's Rio Cibolo */}
          <motion.a
            href="https://sonsriocibolo.com"
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="group block bg-background rounded-2xl overflow-hidden border border-border/50 shadow-soft hover:shadow-medium transition-all hover:-translate-y-1"
          >
            <img
              src={sonsRioCiboloImage}
              alt="Son's Rio Cibolo creekside cabins on Cibolo Creek"
              loading="lazy"
              decoding="async"
              className="w-full h-56 object-cover"
            />
            <div className="p-6 md:p-8">
              <h3 className="font-display text-2xl font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
                Son's Rio Cibolo
              </h3>
              <p className="text-base text-muted-foreground mb-5">
                100+ acres along 2 miles of spring-fed Cibolo Creek.
              </p>
              <ul className="space-y-2 mb-6">
                {cibooFeatures.map(({ icon: Icon, label }) => (
                  <li key={label} className="flex items-start gap-2 text-sm text-foreground">
                    <Icon className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                    <span>{label}</span>
                  </li>
                ))}
              </ul>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-accent group-hover:gap-2 transition-all">
                Plan a Weekend at sonsriocibolo.com
                <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </motion.a>
        </div>
      </div>
    </section>
  );
};

export default OvernightSisterProperties;